import { useEffect, useState } from 'react';
import {
  getSamlProvisionConfig,
  updateSamlProvisionConfig,
  addGroupMapping,
  updateGroupMapping,
  deleteGroupMapping,
  type SamlProvisionConfig,
  type GroupMapping,
  type Organization,
} from '../../../lib/saml-api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Loader2, Plus, Pencil, Trash2, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';

interface Workspace {
  id: string;
  name: string;
}

interface SAMLGroupMappingsProps {
  organization: Organization | null;
  workspaces: Workspace[];
}

export function SAMLGroupMappings({ organization, workspaces }: SAMLGroupMappingsProps) {
  // Config state
  const [config, setConfig] = useState<SamlProvisionConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Draft state for batch saving
  const [draftConfig, setDraftConfig] = useState<{
    enableUserCreation?: boolean;
    enableUserOverride?: boolean;
    unmappedWorkspaceId?: string | null;
    unmappedRole?: string | null;
    attributeMappings?: {
      groups?: string;
      firstName?: string;
      lastName?: string;
      email?: string;
      displayName?: string;
      [key: string]: string | undefined;
    };
  } | null>(null);

  // Add mapping dialog
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addGroupName, setAddGroupName] = useState('');
  const [addWorkspaceId, setAddWorkspaceId] = useState('');
  const [addRole, setAddRole] = useState<'admin' | 'user' | 'viewer'>('user');
  const [adding, setAdding] = useState(false);

  // Edit mapping dialog
  const [editingMapping, setEditingMapping] = useState<GroupMapping | null>(null);
  const [editWorkspaceId, setEditWorkspaceId] = useState('');
  const [editRole, setEditRole] = useState<'admin' | 'user' | 'viewer'>('user');
  const [updating, setUpdating] = useState(false);

  // Delete mapping dialog
  const [deletingMapping, setDeletingMapping] = useState<GroupMapping | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Check if there are unsaved changes
  const hasUnsavedChanges = draftConfig !== null;

  // Load config on mount
  useEffect(() => {
    if (!organization) {
      setLoading(false);
      return;
    }

    loadConfig();
  }, [organization]);

  const loadConfig = async () => {
    try {
      const data = await getSamlProvisionConfig();
      setConfig(data);
      setDraftConfig(null); // Clear any draft changes
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to load SAML configuration');
    } finally {
      setLoading(false);
    }
  };

  // Update draft config (no API call yet)
  const handleDraftChange = (updates: {
    enableUserCreation?: boolean;
    enableUserOverride?: boolean;
    unmappedWorkspaceId?: string | null;
    unmappedRole?: string | null;
    attributeMappings?: {
      groups?: string;
      firstName?: string;
      lastName?: string;
      [key: string]: string | undefined;
    };
  }) => {
    setDraftConfig((prev) => ({
      ...prev,
      ...updates,
    }));
  };

  // Save all changes
  const handleSaveChanges = async () => {
    if (!config || !draftConfig) return;

    setSaving(true);
    try {
      // Build payload with current values (merged draft + saved config)
      const payload = {
        ...draftConfig,
      };

      // If unmapped fields are in draft, ensure both are sent together
      if ('unmappedWorkspaceId' in draftConfig || 'unmappedRole' in draftConfig) {
        payload.unmappedWorkspaceId = getCurrentValue('unmappedWorkspaceId');
        payload.unmappedRole = getCurrentValue('unmappedRole');
      }

      // If toggle fields are in draft, ensure both are sent together
      if ('enableUserCreation' in draftConfig || 'enableUserOverride' in draftConfig) {
        payload.enableUserCreation = getCurrentValue('enableUserCreation');
        payload.enableUserOverride = getCurrentValue('enableUserOverride');
      }

      // If attributeMappings is in draft, send the complete merged object
      if ('attributeMappings' in draftConfig) {
        payload.attributeMappings = getCurrentValue('attributeMappings');
      }

      const updated = await updateSamlProvisionConfig(payload);
      setConfig(updated);
      setDraftConfig(null); // Clear draft after successful save
      toast.success('Configuration saved successfully');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  // Cancel changes
  const handleCancelChanges = () => {
    setDraftConfig(null);
  };

  // Get current value (draft if exists, otherwise saved config)
  const getCurrentValue = <K extends keyof SamlProvisionConfig>(key: K): SamlProvisionConfig[K] => {
    if (draftConfig && key in draftConfig) {
      return draftConfig[key as keyof typeof draftConfig] as SamlProvisionConfig[K];
    }
    return config?.[key] as SamlProvisionConfig[K];
  };

  // Helper to get current attributeMappings (merged draft + saved)
  const getCurrentAttributeMappings = () => {
    const savedMappings = config?.attributeMappings || {};
    const draftMappings = draftConfig?.attributeMappings || {};
    return { ...savedMappings, ...draftMappings };
  };

  // Helper to update a single attribute mapping
  const updateAttributeMapping = (key: string, value: string | null) => {
    // Work with draft mappings only (not merged with saved)
    const draftMappings = draftConfig?.attributeMappings || {};
    const newMappings = { ...draftMappings };

    if (value === null || value === '') {
      // Explicitly set to undefined to override saved value
      newMappings[key] = undefined;
    } else {
      newMappings[key] = value;
    }

    handleDraftChange({ attributeMappings: newMappings });
  };

  const handleAddMapping = async () => {
    if (!addGroupName.trim() || !addWorkspaceId) {
      toast.error('Please fill in all fields');
      return;
    }

    setAdding(true);
    try {
      const updated = await addGroupMapping({
        groupName: addGroupName,
        workspaceId: addWorkspaceId,
        role: addRole,
      });
      setConfig(updated);
      setShowAddDialog(false);
      setAddGroupName('');
      setAddWorkspaceId('');
      setAddRole('user');
      toast.success('Group mapping added');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to add group mapping');
    } finally {
      setAdding(false);
    }
  };

  const handleUpdateMapping = async () => {
    if (!editingMapping) return;

    setUpdating(true);
    try {
      const updated = await updateGroupMapping(editingMapping.id, {
        workspaceId: editWorkspaceId,
        role: editRole,
      });
      setConfig(updated);
      setEditingMapping(null);
      toast.success('Group mapping updated');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update group mapping');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteMapping = async () => {
    if (!deletingMapping) return;

    setDeleting(true);
    try {
      const updated = await deleteGroupMapping(deletingMapping.id);
      setConfig(updated);
      setDeletingMapping(null);
      toast.success('Group mapping deleted');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete group mapping');
    } finally {
      setDeleting(false);
    }
  };

  const getWorkspaceName = (workspaceId: string): string => {
    const workspace = workspaces.find(w => w.id === workspaceId);
    return workspace?.name || workspaceId;
  };

  const getRoleBadgeClass = (role: string): string => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-800';
      case 'user':
        return 'bg-blue-100 text-blue-800';
      case 'viewer':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <div className="flex items-start space-x-3">
          <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-yellow-900">Organization Required</p>
            <p className="text-sm text-yellow-800 mt-1">
              Please create an organization first to configure group mappings.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-sm text-red-800">Failed to load SAML configuration</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Provisioning Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle>SAML Provisioning Settings</CardTitle>
          <CardDescription>
            Control how users are provisioned and updated via SAML authentication
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable User Creation */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-base">Enable User Creation</Label>
              <p className="text-sm text-muted-foreground">
                Automatically create accounts on first SAML login (JIT provisioning)
              </p>
            </div>
            <Switch
              checked={!!getCurrentValue('enableUserCreation')}
              onCheckedChange={(checked) => handleDraftChange({ enableUserCreation: checked })}
              disabled={saving}
            />
          </div>

          {/* Enable User Override */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-base">Enable User Override</Label>
              <p className="text-sm text-muted-foreground">
                Update user workspace and role on every login based on current SAML groups
              </p>
            </div>
            <Switch
              checked={!!getCurrentValue('enableUserOverride')}
              onCheckedChange={(checked) => handleDraftChange({ enableUserOverride: checked })}
              disabled={saving}
            />
          </div>
        </CardContent>
      </Card>

      {/* Unmapped Users Card */}
      <Card>
        <CardHeader>
          <CardTitle>Default Assignment (Unmapped Users)</CardTitle>
          <CardDescription>
            Users whose SAML groups don't match any mappings below. Leave both empty to deny access.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Workspace</Label>
            <Select
              value={getCurrentValue('unmappedWorkspaceId') || '__none__'}
              onValueChange={(value) => {
                // When clearing to "__none__", clear both workspace and role
                // When selecting a workspace, set default role if not already set
                const updates: any = {};
                if (value === '__none__') {
                  updates.unmappedWorkspaceId = null;
                  updates.unmappedRole = null;
                } else {
                  updates.unmappedWorkspaceId = value;
                  // If no role is set yet, default to "user"
                  const currentRole = getCurrentValue('unmappedRole');
                  if (!currentRole) {
                    updates.unmappedRole = 'user';
                  }
                }
                handleDraftChange(updates);
              }}
              disabled={saving}
            >
              <SelectTrigger>
                <SelectValue placeholder="None (deny access)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None (deny access)</SelectItem>
                {workspaces.map((ws) => (
                  <SelectItem key={ws.id} value={ws.id}>
                    {ws.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {getCurrentValue('unmappedWorkspaceId') && (
            <div className="space-y-2">
              <Label>Role</Label>
              <Select
                value={getCurrentValue('unmappedRole') || ''}
                onValueChange={(value) =>
                  handleDraftChange({ unmappedRole: value || null })
                }
                disabled={saving}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="viewer">Viewer</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Visual indicator */}
          {getCurrentValue('unmappedWorkspaceId') && getCurrentValue('unmappedRole') ? (
            <div className="text-sm text-green-600 flex items-center space-x-2 p-3 bg-green-50 rounded-lg">
              <span>✓</span>
              <span>
                Unmapped users will be assigned to <strong>{getWorkspaceName(getCurrentValue('unmappedWorkspaceId') as string)}</strong> as{' '}
                <strong>{getCurrentValue('unmappedRole')}</strong>
              </span>
            </div>
          ) : (
            <div className="text-sm text-yellow-600 flex items-center space-x-2 p-3 bg-yellow-50 rounded-lg">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>Unmapped users will be denied access</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Advanced Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle>Advanced Settings</CardTitle>
          <CardDescription>
            Configure SAML attribute names for user provisioning
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Groups Attribute Name */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="groupsAttributeName">Groups SAML Attribute</Label>
              {getCurrentAttributeMappings().groups && (
                <button
                  type="button"
                  onClick={() => updateAttributeMapping('groups', null)}
                  disabled={saving}
                  className="text-xs text-blue-600 hover:text-blue-700 hover:underline disabled:opacity-50"
                >
                  Use Defaults
                </button>
              )}
            </div>
            <Input
              id="groupsAttributeName"
              value={getCurrentAttributeMappings().groups || ''}
              onChange={(e) => updateAttributeMapping('groups', e.target.value || null)}
              placeholder="groups, memberOf, roles (defaults)"
              disabled={saving}
            />
            <p className="text-xs text-muted-foreground">
              Leave empty to use common defaults: groups, memberOf, http://schemas.microsoft.com/ws/2008/06/identity/claims/groups
            </p>
          </div>

          {/* First Name Attribute */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="firstNameAttribute">First Name SAML Attribute</Label>
              {getCurrentAttributeMappings().firstName && (
                <button
                  type="button"
                  onClick={() => updateAttributeMapping('firstName', null)}
                  disabled={saving}
                  className="text-xs text-blue-600 hover:text-blue-700 hover:underline disabled:opacity-50"
                >
                  Use Defaults
                </button>
              )}
            </div>
            <Input
              id="firstNameAttribute"
              value={getCurrentAttributeMappings().firstName || ''}
              onChange={(e) => updateAttributeMapping('firstName', e.target.value || null)}
              placeholder="givenName, firstName (defaults)"
              disabled={saving}
            />
            <p className="text-xs text-muted-foreground">
              Leave empty to use common defaults: givenName, firstName, given_name, http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname
            </p>
          </div>

          {/* Last Name Attribute */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="lastNameAttribute">Last Name SAML Attribute</Label>
              {getCurrentAttributeMappings().lastName && (
                <button
                  type="button"
                  onClick={() => updateAttributeMapping('lastName', null)}
                  disabled={saving}
                  className="text-xs text-blue-600 hover:text-blue-700 hover:underline disabled:opacity-50"
                >
                  Use Defaults
                </button>
              )}
            </div>
            <Input
              id="lastNameAttribute"
              value={getCurrentAttributeMappings().lastName || ''}
              onChange={(e) => updateAttributeMapping('lastName', e.target.value || null)}
              placeholder="sn, surname, lastName (defaults)"
              disabled={saving}
            />
            <p className="text-xs text-muted-foreground">
              Leave empty to use common defaults: sn, surname, lastName, last_name, familyName, http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Group Mappings Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Group Mappings</CardTitle>
              <CardDescription className="mt-1.5">
                Map SAML groups from your Identity Provider to workspaces and roles
              </CardDescription>
            </div>
            <Button onClick={() => setShowAddDialog(true)} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Mapping
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {config.groupMappings.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <p className="text-sm">No group mappings configured yet.</p>
              <p className="text-sm mt-1">Click "Add Mapping" to create your first mapping.</p>
            </div>
          ) : (
            <div className="border rounded-lg">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left text-sm font-medium p-3">Group Name</th>
                    <th className="text-left text-sm font-medium p-3">Workspace</th>
                    <th className="text-left text-sm font-medium p-3">Role</th>
                    <th className="text-right text-sm font-medium p-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {config.groupMappings.map((mapping) => (
                    <tr key={mapping.id} className="hover:bg-muted/30">
                      <td className="p-3">
                        <code className="text-sm bg-muted px-2 py-1 rounded">
                          {mapping.groupName}
                        </code>
                      </td>
                      <td className="p-3 text-sm">
                        {getWorkspaceName(mapping.workspaceId)}
                      </td>
                      <td className="p-3">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeClass(
                            mapping.role
                          )}`}
                        >
                          {mapping.role}
                        </span>
                      </td>
                      <td className="p-3 text-right space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditingMapping(mapping);
                            setEditWorkspaceId(mapping.workspaceId);
                            setEditRole(mapping.role);
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeletingMapping(mapping)}
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Mapping Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Group Mapping</DialogTitle>
            <DialogDescription>
              Map a SAML group from your Identity Provider to a workspace and role.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="add-group-name">Group Name</Label>
              <Input
                id="add-group-name"
                placeholder="e.g., Engineering, OKTA_OptiDev_Admins"
                value={addGroupName}
                onChange={(e) => setAddGroupName(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                The exact group name as it appears in your Identity Provider
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="add-workspace">Workspace</Label>
              <Select value={addWorkspaceId} onValueChange={setAddWorkspaceId}>
                <SelectTrigger id="add-workspace">
                  <SelectValue placeholder="Select workspace" />
                </SelectTrigger>
                <SelectContent>
                  {workspaces.map((ws) => (
                    <SelectItem key={ws.id} value={ws.id}>
                      {ws.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="add-role">Role</Label>
              <Select value={addRole} onValueChange={(v) => setAddRole(v as any)}>
                <SelectTrigger id="add-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)} disabled={adding}>
              Cancel
            </Button>
            <Button onClick={handleAddMapping} disabled={adding}>
              {adding && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {adding ? 'Adding...' : 'Add Mapping'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Mapping Dialog */}
      <Dialog open={!!editingMapping} onOpenChange={() => setEditingMapping(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Group Mapping</DialogTitle>
            <DialogDescription>
              Update the workspace or role for this SAML group.
            </DialogDescription>
          </DialogHeader>

          {editingMapping && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Group Name</Label>
                <Input value={editingMapping.groupName} disabled />
                <p className="text-xs text-muted-foreground">Group name cannot be changed</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-workspace">Workspace</Label>
                <Select value={editWorkspaceId} onValueChange={setEditWorkspaceId}>
                  <SelectTrigger id="edit-workspace">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {workspaces.map((ws) => (
                      <SelectItem key={ws.id} value={ws.id}>
                        {ws.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-role">Role</Label>
                <Select value={editRole} onValueChange={(v) => setEditRole(v as any)}>
                  <SelectTrigger id="edit-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="viewer">Viewer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingMapping(null)} disabled={updating}>
              Cancel
            </Button>
            <Button onClick={handleUpdateMapping} disabled={updating}>
              {updating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {updating ? 'Updating...' : 'Update Mapping'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deletingMapping} onOpenChange={() => setDeletingMapping(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Group Mapping</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this group mapping? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          {deletingMapping && (
            <div className="py-4">
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm font-medium">Group Name:</p>
                <code className="text-sm">{deletingMapping.groupName}</code>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingMapping(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteMapping} disabled={deleting}>
              {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {deleting ? 'Deleting...' : 'Delete Mapping'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unified Save/Cancel Buttons */}
      {hasUnsavedChanges && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 text-sm text-amber-700">
                <AlertCircle className="h-5 w-5" />
                <span className="font-medium">You have unsaved changes</span>
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  onClick={handleCancelChanges}
                  disabled={saving}
                  className="border-amber-300 hover:bg-amber-100"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveChanges}
                  disabled={saving}
                  variant="default"
                >
                  {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
